import type { LocaleObject } from "@nuxtjs/i18n";
import type { ResolvableLink, ResolvableArray, ResolvableMeta } from "@unhead/vue";

export default defineNuxtRouteMiddleware((to) => {
    const { public: config } = useRuntimeConfig();
    const nuxtApp = useNuxtApp();
    const { locales } = nuxtApp.$i18n;
    const url = to.fullPath;

    // The key values must match the key from Nuxt i18n so we can override it
    // see: https://github.com/nuxt-modules/i18n/blob/main/src/runtime/routing/head.ts
    const localeMap = new Map<string, LocaleObject>();
    for (const locale of locales.value) {
        const [language, region] = locale.code.split("-");
        if (language && region && (locale.isCatchallLocale || !localeMap.has(language))) {
            localeMap.set(language, locale);
        }

        localeMap.set(locale.code, locale);
    }

    const canonicalHref = `${config.baseUrl}${url}`;

    const link: ResolvableArray<ResolvableLink> = [{ id: "i18n-can", rel: "canonical", href: canonicalHref }];

    const meta: ResolvableArray<ResolvableMeta> = [{ id: "i18n-og-url", property: "og:url", content: canonicalHref }];

    const path = url.substring(url.indexOf("/", 1) + 1); // remainder of path without current locale code

    // x-default is English
    link.push({ id: "i18n-xd", href: `${config.baseUrl}/en/${path}`, rel: "alternate", hreflang: "x-default" });

    for (const [language, mapLocale] of localeMap.entries()) {
        const href = `${config.baseUrl}/${mapLocale.code}/${path}`;
        link.push({ id: `i18n-alt-${language}`, href, rel: "alternate", hreflang: language });
    }

    useHead({ link, meta });
});
